/**
 * RequestInfoLinkEditor.tsx
 *
 * The body of Marketing → Templates → Request-Info Links → New link / Edit
 * (routed by RequestInfoLinkBuilderPage). Laid out like the email and SMS
 * template builders: a top bar with the back arrow and the save action, the
 * form on the left and the page preview on the right, each scrolling on its own
 * inside the page's height. When the editor is narrow, the preview gives way
 * first: its panel narrows (the phone in it scales down), and once there is too
 * little room for both, it is left out and the form has the full width.
 *
 * A NEW link: pick ONE resource (a product, package, appointment type or
 * membership), add an optional note, choose the top image (or keep the default)
 * and which branding logo sits on it, and generate. The editor then reopens on
 * the new link, ready to copy. The page shown after the tap uses the same image
 * and logo unless the author picks its own. Under the button, the resource's
 * photo and description can each be hidden, and the description replaced with
 * the author's own words. The photo picker and thumbnails crop every image to the
 * page's photo band, so an image is chosen as it will be seen.
 *
 * An EXISTING link: the note, details, images and logos can change; the
 * resource cannot.
 * The link is already in sent messages, and swapping what it is about would
 * change what those recipients open — a different resource is a new link. The
 * URL never changes, so every message already sent shows the edit.
 *
 * The link box states how many characters personalization adds to an SMS, so the
 * author sees the cost before pasting; Copy always puts the BARE link on the
 * clipboard (the personalized form only ever exists in a sent message).
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Copy, Eye, Image as ImageIcon, Link as LinkIcon, Save } from 'lucide-react';
import { Badge, Button, SearchableCombobox, Select, Switch } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FormField } from '@/components/ui/FormField';
import { Textarea } from '@/components/ui/Textarea';
import { FtpPhotoPicker } from '@/components/ftp-editor/FtpPhotoPicker';
import { useElementSize } from '@/hooks/useElementSize';
import { useToast } from '@/hooks/useToast';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { RequestInfoLink, RequestInfoResourceType } from '@/services/marketing.service';
import type { SelectOption } from '@/types/common.types';
import {
  REQUEST_INFO_DEFAULT_HERO_URL,
  REQUEST_INFO_DESCRIPTION_MAX,
  REQUEST_INFO_HERO_ASPECT,
  heroObjectPosition,
  resolveLogoVariant,
} from '@/pages/request-info/requestInfo.service';
import type {
  RequestInfoBranding,
  RequestInfoConfirmLogoVariant,
  RequestInfoLogoVariant,
  RequestInfoResource,
} from '@/pages/request-info/requestInfo.service';
import RequestInfoLandingPreview from './RequestInfoLandingPreview';
import type { RequestInfoPreviewView } from './RequestInfoLandingPreview';
import {
  KIND_LABEL,
  NOTE_MAX,
  REQUEST_INFO_LINKS_QUERY_KEY,
  RESOURCE_KINDS,
  loadResourceOptions,
} from './requestInfoLinkResources';

type ChosenLogo = Exclude<RequestInfoLogoVariant, 'auto'>;

/** The narrowest the form gets beside the preview: the image-and-logo row needs about this. */
const FORM_MIN_WIDTH = 560;
/** The preview at full size: the phone (402px) plus the panel's padding, with room to spare. */
const PREVIEW_MAX_WIDTH = 480;
/** Below this the phone would be too small to read, so the preview is dropped instead. */
const PREVIEW_MIN_WIDTH = 320;

/**
 * Whether the preview is shown, and how wide, for the width the editor has. The
 * form keeps FORM_MIN_WIDTH and the preview takes what is left, up to
 * PREVIEW_MAX_WIDTH (the phone scales itself down to fit); once less than
 * PREVIEW_MIN_WIDTH is left, the form has the whole width. An unmeasured width
 * (0: before layout, or jsdom) is treated as a wide screen.
 */
function previewLayout(editorWidth: number): { shown: boolean; width: number } {
  if (editorWidth === 0) return { shown: true, width: PREVIEW_MAX_WIDTH };
  const room = Math.min(PREVIEW_MAX_WIDTH, editorWidth - FORM_MIN_WIDTH);
  return room >= PREVIEW_MIN_WIDTH ? { shown: true, width: room } : { shown: false, width: 0 };
}

export default function RequestInfoLinkEditor({
  link,
  justCreated = false,
  branding,
  extraChars,
  onBack,
  onCreated,
  onSaved,
}: {
  /** The link being edited; absent for a new one. */
  link?: RequestInfoLink;
  /** It was generated a moment ago: say so, and show it ready to copy. */
  justCreated?: boolean;
  branding?: RequestInfoBranding;
  /** The most characters personalization adds to one link in an SMS. */
  extraChars: number;
  onBack: () => void;
  onCreated: (link: RequestInfoLink, extraChars: number) => void;
  onSaved: (link: RequestInfoLink) => void;
}) {
  const toast = useToast();

  const [kind, setKind] = useState<RequestInfoResourceType>(link?.resourceType ?? 'product');
  const [resourceId, setResourceId] = useState<number | null>(link?.resourceId ?? null);
  const [note, setNote] = useState(link?.note ?? '');
  /** null = the page's default photo. */
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(link?.heroImageUrl ?? null);
  // 'auto' (a link from before the choice existed, or a new one) starts on what
  // the page draws for it today, so opening a link never counts as a change.
  const [initialLogo] = useState<ChosenLogo>(() => resolveLogoVariant(link?.logoVariant ?? 'auto', branding));
  const [logoVariant, setLogoVariant] = useState<ChosenLogo>(initialLogo);
  /** The page shown after the tap: null / 'same' = as the landing page. */
  const [confirmHeroImageUrl, setConfirmHeroImageUrl] = useState<string | null>(link?.confirmHeroImageUrl ?? null);
  const [confirmLogoVariant, setConfirmLogoVariant] = useState<RequestInfoConfirmLogoVariant>(
    link?.confirmLogoVariant ?? 'same',
  );
  /** What the page shows under the button; everything, in the resource's own words, by default. */
  const [showResourceImage, setShowResourceImage] = useState(link?.showResourceImage ?? true);
  const [showResourceDescription, setShowResourceDescription] = useState(link?.showResourceDescription ?? true);
  const [descriptionSource, setDescriptionSource] = useState<'resource' | 'custom'>(
    link?.customDescription ? 'custom' : 'resource',
  );
  const [customDescription, setCustomDescription] = useState(link?.customDescription ?? '');
  const [confirmCopyDescription, setConfirmCopyDescription] = useState(false);
  const customDescriptionRef = useRef<HTMLTextAreaElement>(null);
  /** Which page's image the photo picker is choosing, while it is open. */
  const [pickerFor, setPickerFor] = useState<'page' | 'confirm' | null>(null);
  /** The preview follows whichever page was changed last. */
  const [previewView, setPreviewView] = useState<RequestInfoPreviewView>('request');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /**
   * Leaving after a save, a create or a confirmed discard. Held in state so the
   * unsaved-changes guard is switched off by a render BEFORE the navigation
   * runs — navigating straight from the handler would trip the guard's prompt.
   */
  const [pendingExit, setPendingExit] = useState<(() => void) | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const preview = previewLayout(useElementSize(bodyRef).width);

  const kindMeta = RESOURCE_KINDS.find((k) => k.value === kind) ?? RESOURCE_KINDS[0];

  const resourcesQuery = useQuery({
    queryKey: [...REQUEST_INFO_LINKS_QUERY_KEY, 'resources', kind],
    queryFn: () => loadResourceOptions(kind),
    staleTime: 5 * 60_000,
    // An existing link's resource is fixed, so there is nothing to pick.
    enabled: link === undefined,
  });
  const resourceOptions = resourcesQuery.data ?? [];

  // The chosen resource as the page will show it (description, image), for the preview.
  const resourcePreviewQuery = useQuery({
    queryKey: [...REQUEST_INFO_LINKS_QUERY_KEY, 'resource-preview', kind, resourceId],
    queryFn: () => marketingSvc.getRequestInfoResource(kind, resourceId as number),
    enabled: resourceId != null,
    staleTime: 5 * 60_000,
    // A resource made inactive since the link was generated answers 404; the
    // preview then names it from the link, as the page itself does.
    retry: false,
  });

  const resourceName = link ? link.resourceName : resourceOptions.find((o) => o.value === resourceId)?.label;
  const previewResource: RequestInfoResource | undefined =
    resourceId == null
      ? undefined
      : resourcePreviewQuery.data?.resource
        ?? (resourceName ? { kind, name: resourceName, description: '', price: null, imageUrl: null } : undefined);

  const trimmedNote = note.trim().slice(0, NOTE_MAX);
  const lookChanged =
    heroImageUrl !== (link?.heroImageUrl ?? null)
    || logoVariant !== initialLogo
    || confirmHeroImageUrl !== (link?.confirmHeroImageUrl ?? null)
    || confirmLogoVariant !== (link?.confirmLogoVariant ?? 'same');
  const details = {
    showResourceImage,
    showResourceDescription,
    // Only a chosen "write your own" is sent; '' is the resource's own description.
    customDescription: descriptionSource === 'custom' ? customDescription.trim().slice(0, REQUEST_INFO_DESCRIPTION_MAX) : '',
  };
  const detailsChanged =
    details.showResourceImage !== (link?.showResourceImage ?? true)
    || details.showResourceDescription !== (link?.showResourceDescription ?? true)
    || details.customDescription !== (link?.customDescription ?? '');
  const dirty = link
    ? trimmedNote !== (link.note ?? '') || lookChanged || detailsChanged
    : resourceId != null || trimmedNote !== '' || lookChanged || detailsChanged;
  const look = { heroImageUrl, logoVariant, confirmHeroImageUrl, confirmLogoVariant };
  const resourceHasImage = Boolean(previewResource?.imageUrl);
  const resourceHasDescription = Boolean(previewResource?.description?.trim());

  // Browser back, refresh and other in-app navigation, like the template builders.
  useUnsavedChangesGuard(dirty && pendingExit === null);
  useEffect(() => {
    if (pendingExit) pendingExit();
  }, [pendingExit]);
  const leave = (action: () => void) => setPendingExit(() => action);

  const logoOptions: Array<LogoOption<ChosenLogo>> = [
    { value: 'none', label: 'No logo', url: null },
    { value: 'dark', label: 'Dark logo', url: branding?.logoUrl ?? null, needsLogo: true },
    { value: 'light', label: 'Light logo', url: branding?.lightLogoUrl ?? null, onDark: true, needsLogo: true },
  ];
  // "Same as the landing page" shows the logo it currently stands for.
  const pageLogo = logoOptions.find((o) => o.value === logoVariant);
  const confirmLogoOptions: Array<LogoOption<RequestInfoConfirmLogoVariant>> = [
    { value: 'same', label: 'Same as the landing page', url: pageLogo?.url ?? null, onDark: pageLogo?.onDark },
    ...logoOptions,
  ];

  const createMut = useMutation({
    mutationFn: (data: marketingSvc.CreateRequestInfoLinkData) => marketingSvc.createRequestInfoLink(data),
    onSuccess: (res) => {
      toast.success('Link generated');
      leave(() => onCreated(res.link, res.personalizedExtraChars ?? extraChars));
    },
    // Inline rather than a toast, and the form keeps its values, so the author
    // can correct the one thing the API refused (a 422 names it) and retry.
    onError: (err) => setFormError(getErrorMessage(err, 'Could not generate the link. Please try again.')),
  });

  const saveMut = useMutation({
    mutationFn: (data: marketingSvc.UpdateRequestInfoLinkData) =>
      marketingSvc.updateRequestInfoLink((link as RequestInfoLink).id, data),
    onSuccess: (res) => {
      toast.success('Changes saved');
      leave(() => onSaved(res.link));
    },
    onError: (err) => setFormError(getErrorMessage(err, 'Could not save your changes. Please try again.')),
  });

  function pickPageImage(url: string | null) {
    setHeroImageUrl(url);
    setPreviewView('request');
  }

  function pickConfirmImage(url: string | null) {
    setConfirmHeroImageUrl(url);
    setPreviewView('confirmation');
  }

  function handleKindChange(next: RequestInfoResourceType) {
    setKind(next);
    // An id from one kind means nothing in another.
    setResourceId(null);
    setFormError(null);
  }

  /** A change under the button shows on the landing page, so the preview turns to it. */
  function changeDetails(apply: () => void) {
    apply();
    setPreviewView('request');
  }

  /** The resource's own description, as the page would show it and the box can hold. */
  const resourceDescriptionText = (previewResource?.description ?? '').trim().slice(0, REQUEST_INFO_DESCRIPTION_MAX);

  /** A starting point to edit from. Asks first when it would replace something already written. */
  function copyResourceDescription() {
    const current = customDescription.trim();
    if (current !== '' && current !== resourceDescriptionText) {
      setConfirmCopyDescription(true);
      return;
    }
    applyResourceDescription();
  }

  function applyResourceDescription() {
    changeDetails(() => {
      setCustomDescription(resourceDescriptionText);
      setFormError(null);
    });
    // Straight into editing it, at the end of the text.
    window.requestAnimationFrame(() => {
      const box = customDescriptionRef.current;
      if (!box) return;
      box.focus();
      box.setSelectionRange(box.value.length, box.value.length);
    });
  }

  function handleSubmit() {
    // "Write your own" with nothing written would quietly show the resource's.
    if (showResourceDescription && descriptionSource === 'custom' && details.customDescription === '') {
      setFormError(`Write your description, or use the ${kindMeta.noun}'s.`);
      return;
    }
    if (link) {
      if (!dirty) return;
      setFormError(null);
      saveMut.mutate({ note: trimmedNote, ...look, ...details });
      return;
    }
    // The combobox's asterisk is cosmetic; this is the real check.
    if (resourceId == null) {
      setFormError(`Choose a ${kindMeta.noun} first.`);
      return;
    }
    setFormError(null);
    createMut.mutate({ resourceType: kind, resourceId, note: trimmedNote, ...look, ...details });
  }

  function handleBack() {
    if (dirty) setConfirmDiscard(true);
    else onBack();
  }

  async function copyLink(target: RequestInfoLink) {
    try {
      await navigator.clipboard.writeText(target.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link', 'Select it and copy it manually.');
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back to links"
            title="Back to links"
            className="rounded p-1 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          >
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <LinkIcon className="h-4 w-4 flex-shrink-0 text-sky-600" aria-hidden="true" />
          <h1 className="truncate font-semibold text-slate-800">
            {link ? `Edit Link: ${link.resourceName || 'Untitled'}` : 'Create Request-Info Link'}
          </h1>
          {link && (
            <Badge variant={link.active ? 'success' : 'neutral'} dot size="sm">
              {link.active ? 'Active' : 'Inactive'}
            </Badge>
          )}
          {link && dirty && (
            <div className="flex flex-shrink-0 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
              Unsaved changes
            </div>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {link ? (
            <Button
              size="sm"
              leftIcon={<Save className="h-4 w-4" />}
              loading={saveMut.isPending}
              disabled={!dirty || saveMut.isPending}
              onClick={handleSubmit}
            >
              Save changes
            </Button>
          ) : (
            <Button
              size="sm"
              leftIcon={<LinkIcon className="h-4 w-4" />}
              loading={createMut.isPending}
              disabled={resourceId == null || createMut.isPending}
              onClick={handleSubmit}
            >
              Generate link
            </Button>
          )}
        </div>
      </div>

      {/* ── Form + preview, side by side, each scrolling on its own ──────────
          The preview's width comes from the room this editor has (see
          previewLayout), so it narrows, then goes, before the form is squeezed.
          Both panels are `relative` so they contain their absolutely positioned
          content (the sr-only legends, for one). Otherwise that content is
          positioned against the app's <main>, escapes the panel's scroll, and
          makes the whole page taller than the screen once the form is long. */}
      <div ref={bodyRef} className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="relative min-w-0 flex-1 overflow-y-auto">
          <div className="space-y-4 p-5">
            <p className="max-w-3xl text-sm text-slate-500">
              {link
                ? 'Change the images, the logos or the details. The link stays the same, so messages already sent show the change.'
                : 'Pick what the link is about. Whoever taps it sees a page for it and can ask for more information in one tap, which creates a lead. One link works in any number of campaigns, automation steps and templates — each lead is credited to the message that delivered it.'}
            </p>

            {link && (
              <div
                role="region"
                aria-labelledby="request-info-link-heading"
                className={cn(
                  'rounded-lg border p-4',
                  justCreated ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50',
                )}
              >
                <p
                  id="request-info-link-heading"
                  className={cn('text-sm font-semibold', justCreated ? 'text-emerald-900' : 'text-slate-900')}
                >
                  {justCreated ? `Link ready for ${link.resourceName || 'this resource'}` : 'Link'}
                </p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <code
                    data-testid="request-info-generated-url"
                    className="min-w-0 flex-1 select-all break-all rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-slate-800"
                  >
                    {link.url}
                  </code>
                  <Button
                    variant="secondary"
                    leftIcon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    onClick={() => void copyLink(link)}
                  >
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                  <Button
                    variant="ghost"
                    leftIcon={<Eye className="h-4 w-4" />}
                    onClick={() => window.open(link.previewUrl || link.url, '_blank', 'noopener,noreferrer')}
                  >
                    Preview page
                  </Button>
                </div>
                <p className={cn('mt-2 text-xs leading-relaxed', justCreated ? 'text-emerald-900/80' : 'text-slate-500')}>
                  Paste it into any SMS, email, automation step or template. Each recipient is sent their own
                  personalized copy, which adds up to {extraChars} characters per recipient in SMS.
                </p>
              </div>
            )}

            {link ? (
              <FormField
                label={KIND_LABEL[link.resourceType] ?? 'Resource'}
                helpText="A link always points at what it was made for. For something else, create a new link."
              >
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                  {link.resourceName || 'Untitled'}
                </p>
              </FormField>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_minmax(0,1fr)]">
                <FormField label="Resource type" htmlFor="request-info-kind">
                  <select
                    id="request-info-kind"
                    className="form-input-base"
                    value={kind}
                    onChange={(e) => handleKindChange(e.target.value as RequestInfoResourceType)}
                  >
                    {RESOURCE_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                </FormField>

                <SearchableCombobox<number>
                  id="request-info-resource"
                  label={kindMeta.label}
                  required
                  placeholder={`Choose a ${kindMeta.noun}…`}
                  options={resourceOptions}
                  loading={resourcesQuery.isLoading}
                  value={resourceId}
                  onChange={(v) => {
                    setResourceId(v);
                    setFormError(null);
                  }}
                  emptyMessage={
                    resourcesQuery.isError ? `Could not load ${kindMeta.plural}` : `No active ${kindMeta.plural} found`
                  }
                  hint={
                    kind === 'appointmentType'
                      ? 'The page asks about a kind of appointment, not a specific booking.'
                      : undefined
                  }
                  error={
                    resourcesQuery.isError ? `Could not load ${kindMeta.plural}. Reload the page to try again.` : undefined
                  }
                />
              </div>
            )}

            <LookSection
              id="request-info-page-look"
              title="Landing page"
              intro="The photo across the top of the page and the logo on it. Pick a photo from the library, upload one, or keep the default."
            >
              <ImageAndLogo>
                <ImageChoice
                  imageUrl={heroImageUrl}
                  fallbackUrl={REQUEST_INFO_DEFAULT_HERO_URL}
                  fallbackText="Default image"
                  fallbackAlt="Default top image"
                  chosenAlt="Chosen top image"
                  resetLabel="Default"
                  onChoose={() => setPickerFor('page')}
                  onReset={() => pickPageImage(null)}
                />
                <LogoChoice
                  id="request-info-logo"
                  options={logoOptions}
                  value={logoVariant}
                  onChange={(v) => {
                    setLogoVariant(v);
                    setPreviewView('request');
                  }}
                  helpText="Sits on the top-left corner of the photo. Logos come from your account branding."
                />
              </ImageAndLogo>
            </LookSection>

            <LookSection
              id="request-info-details"
              title="Details"
              intro={`What the landing page shows under the button, from the ${kindMeta.noun} and your note.`}
            >
              <ToggleRow
                id="request-info-show-image"
                label={`Show the ${kindMeta.noun}'s photo`}
                hint={previewResource && !resourceHasImage ? `This ${kindMeta.noun} has no photo, so none is shown.` : undefined}
                checked={showResourceImage}
                onChange={(on) => changeDetails(() => setShowResourceImage(on))}
              />
              <ToggleRow
                id="request-info-show-description"
                label="Show a description"
                checked={showResourceDescription}
                onChange={(on) => changeDetails(() => {
                  setShowResourceDescription(on);
                  setFormError(null);
                })}
              />
              {showResourceDescription && (
                <fieldset className="space-y-2 pl-12">
                  <legend className="sr-only">Which description</legend>
                  {(
                    [
                      ['resource', `The ${kindMeta.noun}'s description`],
                      ['custom', 'Write your own'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                      <input
                        type="radio"
                        name="request-info-description-source"
                        value={value}
                        checked={descriptionSource === value}
                        onChange={() => changeDetails(() => {
                          setDescriptionSource(value);
                          setFormError(null);
                        })}
                        className="h-4 w-4 border-slate-300 text-sky-600 focus:ring-sky-400"
                      />
                      {label}
                    </label>
                  ))}
                  {descriptionSource === 'resource' && previewResource && !resourceHasDescription && (
                    <p className="text-xs text-slate-500">This {kindMeta.noun} has no description, so none is shown.</p>
                  )}
                  {descriptionSource === 'custom' && (
                    <div>
                      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <label htmlFor="request-info-custom-description" className="text-sm font-medium text-slate-700">
                          Your description
                        </label>
                        {resourceHasDescription && (
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<Copy className="h-3.5 w-3.5" />}
                            title={`Put the ${kindMeta.noun}'s description in the box, to edit from there`}
                            onClick={copyResourceDescription}
                          >
                            Copy the {kindMeta.noun}&apos;s description
                          </Button>
                        )}
                      </div>
                      <Textarea
                        ref={customDescriptionRef}
                        id="request-info-custom-description"
                        hint={`Shown in place of the ${kindMeta.noun}'s own.`}
                        rows={4}
                        autoResize
                        maxLength={REQUEST_INFO_DESCRIPTION_MAX}
                        showCount
                        value={customDescription}
                        onChange={(e) => changeDetails(() => {
                          setCustomDescription(e.target.value.slice(0, REQUEST_INFO_DESCRIPTION_MAX));
                          setFormError(null);
                        })}
                      />
                    </div>
                  )}
                </fieldset>
              )}
              <Textarea
                id="request-info-note"
                label="Note (optional)"
                hint="Shown on the page under the details, e.g. an offer or what to expect."
                rows={3}
                maxLength={NOTE_MAX}
                showCount
                value={note}
                onChange={(e) => changeDetails(() => setNote(e.target.value.slice(0, NOTE_MAX)))}
              />
            </LookSection>

            <LookSection
              id="request-info-confirm-look"
              title="After tapping"
              intro="The page shown once they tap. It uses the landing page's image and logo unless you pick others."
            >
              <ImageAndLogo>
                <ImageChoice
                  imageUrl={confirmHeroImageUrl}
                  fallbackUrl={heroImageUrl || REQUEST_INFO_DEFAULT_HERO_URL}
                  fallbackText="Same as landing page"
                  fallbackAlt="Same top image as the landing page"
                  chosenAlt="Chosen after-tapping image"
                  resetLabel="Same as page"
                  onChoose={() => setPickerFor('confirm')}
                  onReset={() => pickConfirmImage(null)}
                />
                <LogoChoice
                  id="request-info-confirm-logo"
                  options={confirmLogoOptions}
                  value={confirmLogoVariant}
                  onChange={(v) => {
                    setConfirmLogoVariant(v);
                    setPreviewView('confirmation');
                  }}
                />
              </ImageAndLogo>
            </LookSection>

            {formError && (
              <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {formError}
              </p>
            )}
          </div>
        </div>

        {preview.shown && (
          <aside
            aria-label="Landing page preview"
            className="relative flex flex-shrink-0 flex-col items-center overflow-hidden bg-slate-100 px-4 py-6"
            style={{ width: preview.width }}
          >
            <RequestInfoLandingPreview
              view={previewView}
              onViewChange={setPreviewView}
              resource={previewResource}
              note={trimmedNote}
              branding={branding}
              {...look}
              {...details}
            />
          </aside>
        )}
      </div>

      <FtpPhotoPicker
        crop={{
          aspectRatio: REQUEST_INFO_HERO_ASPECT,
          hint: 'Each photo is cropped as it will be across the top of the page on a phone.',
        }}
        isOpen={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        onSelect={(url) => (pickerFor === 'confirm' ? pickConfirmImage(url) : pickPageImage(url))}
      />

      <ConfirmDialog
        isOpen={confirmCopyDescription}
        title="Replace your description?"
        message={`What you have written will be replaced with the ${kindMeta.noun}'s description, which you can then edit.`}
        confirmLabel="Replace"
        variant="warning"
        onConfirm={applyResourceDescription}
        onClose={() => setConfirmCopyDescription(false)}
      />

      <ConfirmDialog
        isOpen={confirmDiscard}
        title="Discard changes?"
        message={link ? 'Your changes to this link have not been saved.' : 'This link has not been generated yet.'}
        confirmLabel="Discard"
        variant="warning"
        onConfirm={() => leave(onBack)}
        onClose={() => setConfirmDiscard(false)}
      />
    </div>
  );
}

// ─── Look pieces ──────────────────────────────────────────────────────────────

/** A titled group of the image and logo choices for one of the two pages. */
function LookSection({ id, title, intro, children }: { id: string; title: string; intro: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-4 border-t border-slate-200 pt-4">
      <div>
        <h2 id={id} className="text-sm font-semibold text-slate-900">
          {title}
        </h2>
        <p className="mt-0.5 text-xs text-slate-500">{intro}</p>
      </div>
      {children}
    </section>
  );
}

/** The top photo and the logo on it, side by side: the pair a page's look is made of. */
function ImageAndLogo({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-start gap-x-8 gap-y-4">{children}</div>;
}

/**
 * The top photo — the chosen one, or what the page falls back to without one —
 * with the picker button under it and, once a photo is chosen, the button that
 * goes back to the fallback beside it.
 */
function ImageChoice({
  imageUrl,
  fallbackUrl,
  fallbackText,
  fallbackAlt,
  chosenAlt,
  resetLabel,
  onChoose,
  onReset,
}: {
  /** null = the fallback. */
  imageUrl: string | null;
  fallbackUrl: string;
  /** Said beside the button while the fallback is showing. */
  fallbackText: string;
  fallbackAlt: string;
  chosenAlt: string;
  resetLabel: string;
  onChoose: () => void;
  onReset: () => void;
}) {
  return (
    <FormField label="Top image" className="w-60 flex-shrink-0">
      {/* Cropped exactly as the page's photo band crops it on a phone. */}
      <img
        src={imageUrl || fallbackUrl}
        alt={imageUrl ? chosenAlt : fallbackAlt}
        className="w-full rounded-md border border-slate-200 object-cover"
        style={{ aspectRatio: REQUEST_INFO_HERO_ASPECT, objectPosition: heroObjectPosition(imageUrl || fallbackUrl) }}
      />
      <div className="mt-1 flex items-center gap-2">
        <Button size="sm" variant="secondary" leftIcon={<ImageIcon className="h-3.5 w-3.5" />} onClick={onChoose}>
          Image
        </Button>
        {imageUrl ? (
          <Button size="sm" variant="ghost" onClick={onReset}>
            {resetLabel}
          </Button>
        ) : (
          <p className="whitespace-nowrap text-xs text-slate-500">{fallbackText}</p>
        )}
      </div>
    </FormField>
  );
}

/** A switch with its label beside it; the label toggles it too. */
function ToggleRow({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} size="md" checked={checked} onChange={onChange} className="mt-0.5" />
      <div className="min-w-0">
        <label htmlFor={id} className="cursor-pointer text-sm font-medium text-slate-700">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
  );
}

interface LogoOption<V extends string> {
  value: V;
  label: string;
  /** The logo this option puts on the photo, shown beside its label; null = none. */
  url: string | null;
  /** Shown on a dark chip: the light logo is made for dark backgrounds. */
  onDark?: boolean;
  /** Set on the dark and light logos: the option is off when the account has no logo of that kind. */
  needsLogo?: boolean;
}

/**
 * Which branding logo sits on the photo, as a dropdown that shows each logo
 * beside its name, in the list and in the closed field. A logo the account's
 * branding does not have stays listed, but cannot be picked, and says why.
 */
function LogoChoice<V extends string>({
  id,
  options,
  value,
  onChange,
  helpText,
}: {
  id: string;
  options: Array<LogoOption<V>>;
  value: V;
  onChange: (value: V) => void;
  helpText?: string;
}) {
  const byValue = new Map(options.map((o) => [o.value, o]));
  const row = (opt: SelectOption<V>) => {
    const logo = byValue.get(opt.value);
    return (
      <span className="flex min-w-0 items-center gap-3">
        <LogoChip url={logo?.url ?? null} onDark={logo?.onDark} />
        <span className="min-w-0 truncate">{opt.label}</span>
      </span>
    );
  };

  return (
    <Select<V>
      id={id}
      label="Logo"
      hint={helpText}
      wrapperClassName="min-w-[14rem] max-w-xs flex-1"
      options={options.map((o) => {
        const unavailable = Boolean(o.needsLogo) && !o.url;
        return {
          value: o.value,
          label: unavailable ? `${o.label} (not in your branding)` : o.label,
          disabled: unavailable,
        };
      })}
      value={value}
      onChange={(v) => {
        if (v !== null) onChange(v);
      }}
      renderOption={row}
      renderValue={row}
    />
  );
}

/** A logo on the kind of background it was made for; an empty dashed box for none. */
function LogoChip({ url, onDark = false }: { url: string | null; onDark?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-7 w-14 flex-shrink-0 items-center justify-center rounded px-1',
        url ? (onDark ? 'bg-slate-700' : 'bg-slate-100') : 'border border-dashed border-slate-300',
      )}
    >
      {url && <img src={url} alt="" className="max-h-5 max-w-full object-contain" />}
    </span>
  );
}
